import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, updateDoc, doc, orderBy, onSnapshot, addDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import RequestDetailModal from './RequestDetailModal';
import { notificationModel } from '../../data/notificationModel';
import { reserveVideoCallUsage, refundVideoCallUsage } from '../../utils/videoCallDatabase';
import { checkVideoCallPackage } from '../../utils/videoCallUtils';
import { VideoCallIcon } from '../Layout/ProfileMenuDropdown';

const VideoCallRequestsList = ({ storeId }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, scheduled, active, completed, cancelled
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    console.log('🔍 VideoCallRequestsList useEffect:', { storeId, filter });
    if (storeId) {
      loadRequests();
      // Subscribe to real-time updates
      const unsubscribe = subscribeToRequests();
      return () => {
        console.log('🧹 Cleaning up subscription');
        unsubscribe();
      };
    } else {
      console.warn('⚠️ VideoCallRequestsList: No storeId, cannot load requests');
      setLoading(false);
    }
  }, [storeId, filter]);

  const loadRequests = async () => {
    if (!storeId) {
      console.warn('⚠️ VideoCallRequestsList: No storeId provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('🔍 Loading video call requests for storeId:', storeId);
      
      const requestsRef = collection(db, 'videoCallRequests');
      let q = query(
        requestsRef,
        where('storeId', '==', storeId),
        orderBy('createdAt', 'desc')
      );

      try {
        const snapshot = await getDocs(q);
        console.log('📊 Found', snapshot.size, 'video call requests for storeId:', storeId);
        
        // Debug: Check all requests in collection (without filter)
        try {
          const allRequestsRef = collection(db, 'videoCallRequests');
          const allSnapshot = await getDocs(allRequestsRef);
          console.log('🔍 DEBUG: Total requests in collection:', allSnapshot.size);
          console.log('🔍 DEBUG: Looking for storeId:', storeId);
          allSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const requestStoreId = data.storeId;
            const matches = requestStoreId === storeId;
            console.log('🔍 All Request:', {
              id: doc.id,
              storeId: requestStoreId,
              matches: matches ? '✅ MATCH' : '❌ NO MATCH',
              buyerId: data.buyerId,
              status: data.status,
              productName: data.productName,
              createdAt: data.createdAt
            });
          });
        } catch (debugError) {
          console.error('Error checking all requests:', debugError);
        }
        
        const requestsData = snapshot.docs.map(doc => {
          const data = doc.data();
          console.log('📝 Request:', doc.id, {
            storeId: data.storeId,
            status: data.status,
            buyerName: data.buyerName,
            productName: data.productName
          });
          return {
            id: doc.id,
            ...data
          };
        });

        setRequests(requestsData);
      } catch (indexError) {
        // If index is missing, try without orderBy
        if (indexError.code === 'failed-precondition') {
          console.warn('⚠️ Index missing, trying without orderBy...');
          const fallbackQ = query(
            requestsRef,
            where('storeId', '==', storeId)
          );
          
          const snapshot = await getDocs(fallbackQ);
          console.log('📊 Found', snapshot.size, 'video call requests (without orderBy)');
          
          const requestsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Sort manually
          requestsData.sort((a, b) => {
            const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return bTime - aTime;
          });
          
          setRequests(requestsData);
          console.warn('💡 Please create Firestore index: videoCallRequests (storeId, createdAt)');
        } else {
          throw indexError;
        }
      }
    } catch (error) {
      console.error('❌ Error loading video call requests:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        storeId: storeId
      });
    } finally {
      setLoading(false);
    }
  };

  const subscribeToRequests = () => {
    if (!storeId) {
      console.warn('⚠️ VideoCallRequestsList: No storeId for subscription');
      return () => {};
    }

    const requestsRef = collection(db, 'videoCallRequests');
    const q = query(
      requestsRef,
      where('storeId', '==', storeId),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, 
      (snapshot) => {
        console.log('🔄 Real-time update: Found', snapshot.size, 'requests');
        const requestsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRequests(requestsData);
      },
      (error) => {
        console.error('❌ Error in real-time subscription:', error);
        if (error.code === 'failed-precondition') {
          console.warn('⚠️ Firestore index may be missing. Please create index for: videoCallRequests (storeId, createdAt)');
        }
      }
    );
  };

  const handleApprove = async (requestId) => {
    try {
      const request = requests.find(r => r.id === requestId);
      if (!request) {
        alert('ไม่พบ request');
        return;
      }

      // Check if buyer has enough calls remaining
      const packageInfo = await checkVideoCallPackage(request.buyerId);
      if (!packageInfo.hasPackage) {
        alert(t('videoCall.buyerNoPackage'));
        return;
      }

      if (packageInfo.callsRemaining === 0 && packageInfo.callsLimit !== -1) {
        alert(t('videoCall.buyerLimitReached'));
        return;
      }

      // Reserve video call usage (หักจำนวน)
      try {
        await reserveVideoCallUsage(request.buyerId, requestId);
        console.log('✅ Reserved video call usage for buyer:', request.buyerId);
      } catch (usageError) {
        console.error('Error reserving video call usage:', usageError);
        alert('เกิดข้อผิดพลาดในการหักจำนวน Video Call: ' + usageError.message);
        return;
      }

      const requestRef = doc(db, 'videoCallRequests', requestId);
      await updateDoc(requestRef, {
        status: 'approved',
        scheduledTime: request.requestedTime || request.scheduledTime,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Create notification for buyer
      try {
        console.log('📤 Creating notification for buyer (approved):', {
          buyerId: request.buyerId,
          requestId: requestId,
          productName: request.productName
        });
        
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          request.buyerId, // Buyer
          notificationModel.types.VIDEO_CALL_APPROVED,
          '✅ Video Call ถูกอนุมัติแล้ว',
          `ร้านค้าได้อนุมัติ Video Call สำหรับสินค้า "${request.productName || 'N/A'}" แล้ว`,
          {
            requestId: requestId,
            storeId: request.storeId,
            productId: request.productId,
            scheduledTime: request.scheduledTime || request.requestedTime
          }
        );
        
        // Ensure read field is explicitly set to false
        const notificationData = {
          ...notification,
          read: false, // Explicitly set to false
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        const notifDoc = await addDoc(notificationsRef, notificationData);
        
        console.log('✅ Notification created successfully:', {
          notificationId: notifDoc.id,
          userId: request.buyerId,
          type: notificationModel.types.VIDEO_CALL_APPROVED
        });
      } catch (notifError) {
        console.error('❌ Error creating notification:', notifError);
        console.error('Error details:', {
          buyerId: request.buyerId,
          errorCode: notifError.code,
          errorMessage: notifError.message
        });
      }

      alert(t('videoCall.approveRequestSuccess'));
    } catch (error) {
      console.error('Error approving request:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleReject = async (requestId, reason) => {
    try {
      const request = requests.find(r => r.id === requestId);
      if (!request) {
        alert('ไม่พบ request');
        return;
      }

      // If request was already approved, refund the usage
      if (request.status === 'approved' || request.status === 'scheduled') {
        try {
          await refundVideoCallUsage(request.buyerId, requestId);
          console.log('✅ Refunded video call usage for buyer:', request.buyerId);
        } catch (usageError) {
          console.error('Error refunding video call usage:', usageError);
          // Continue anyway - don't block rejection
        }
      }

      const requestRef = doc(db, 'videoCallRequests', requestId);
      await updateDoc(requestRef, {
        status: 'rejected',
        rejectionReason: reason,
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Create notification for buyer
      try {
        console.log('📤 Creating notification for buyer (rejected):', {
          buyerId: request.buyerId,
          requestId: requestId,
          productName: request.productName,
          reason: reason
        });
        
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          request.buyerId, // Buyer
          notificationModel.types.VIDEO_CALL_REJECTED,
          '❌ Video Call ถูกปฏิเสธ',
          `ร้านค้าได้ปฏิเสธ Video Call สำหรับสินค้า "${request.productName || 'N/A'}"${reason ? `: ${reason}` : ''}`,
          {
            requestId: requestId,
            storeId: request.storeId,
            productId: request.productId,
            rejectionReason: reason
          }
        );
        
        // Ensure read field is explicitly set to false
        const notificationData = {
          ...notification,
          read: false, // Explicitly set to false
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        const notifDoc = await addDoc(notificationsRef, notificationData);
        
        console.log('✅ Notification created successfully:', {
          notificationId: notifDoc.id,
          userId: request.buyerId,
          type: notificationModel.types.VIDEO_CALL_REJECTED
        });
      } catch (notifError) {
        console.error('❌ Error creating notification:', notifError);
        console.error('Error details:', {
          buyerId: request.buyerId,
          errorCode: notifError.code,
          errorMessage: notifError.message
        });
      }

      alert(t('videoCall.rejectRequestSuccess'));
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedRequests.length === 0) return;
    
    try {
      const updatePromises = selectedRequests.map(async (requestId) => {
        const request = requests.find(r => r.id === requestId);
        if (!request) return;

        // Check and reserve usage for each request
        const packageInfo = await checkVideoCallPackage(request.buyerId);
        if (packageInfo.hasPackage && (packageInfo.callsRemaining > 0 || packageInfo.callsLimit === -1)) {
          await reserveVideoCallUsage(request.buyerId, requestId);
        }

        const requestRef = doc(db, 'videoCallRequests', requestId);
        await updateDoc(requestRef, {
          status: 'approved',
          scheduledTime: request.requestedTime || request.scheduledTime,
          approvedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        // Create notification for buyer
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            request.buyerId,
            notificationModel.types.VIDEO_CALL_APPROVED,
            '✅ Video Call ถูกอนุมัติแล้ว',
            `ร้านค้าได้อนุมัติ Video Call สำหรับสินค้า "${request.productName || 'N/A'}" แล้ว`,
            {
              requestId: requestId,
              storeId: request.storeId,
              productId: request.productId
            }
          );
          
          await addDoc(notificationsRef, {
            ...notification,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifError) {
          console.error('Error creating notification:', notifError);
        }
      });
      
      await Promise.all(updatePromises);
      alert(t('videoCall.approveMultipleRequestsSuccess', { count: selectedRequests.length }));
      setSelectedRequests([]);
    } catch (error) {
      console.error('Error bulk approving:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleBulkReject = async (reason) => {
    if (selectedRequests.length === 0) return;
    
    try {
      const updatePromises = selectedRequests.map(async (requestId) => {
        const request = requests.find(r => r.id === requestId);
        if (!request) return;

        // If request was already approved, refund the usage
        if (request.status === 'approved' || request.status === 'scheduled') {
          try {
            await refundVideoCallUsage(request.buyerId, requestId);
          } catch (usageError) {
            console.error('Error refunding video call usage:', usageError);
          }
        }

        const requestRef = doc(db, 'videoCallRequests', requestId);
        await updateDoc(requestRef, {
          status: 'rejected',
          rejectionReason: reason,
          rejectedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        // Create notification for buyer
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            request.buyerId,
            notificationModel.types.VIDEO_CALL_REJECTED,
            '❌ Video Call ถูกปฏิเสธ',
            `ร้านค้าได้ปฏิเสธ Video Call สำหรับสินค้า "${request.productName || 'N/A'}"${reason ? `: ${reason}` : ''}`,
            {
              requestId: requestId,
              storeId: request.storeId,
              productId: request.productId,
              rejectionReason: reason
            }
          );
          
          await addDoc(notificationsRef, {
            ...notification,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifError) {
          console.error('Error creating notification:', notifError);
        }
      });
      
      await Promise.all(updatePromises);
      alert(t('videoCall.rejectMultipleRequestsSuccess', { count: selectedRequests.length }));
      setSelectedRequests([]);
    } catch (error) {
      console.error('Error bulk rejecting:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const filteredRequests = filter === 'all' 
    ? requests 
    : requests.filter(r => r.status === filter);

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      scheduled: 'bg-green-100 text-green-800',
      active: 'bg-purple-100 text-purple-800',
      completed: 'bg-gray-100 text-gray-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    const labels = {
      pending: t('videoCall.pending'),
      approved: t('videoCall.approved'),
      scheduled: t('videoCall.scheduled'),
      active: t('videoCall.active'),
      completed: t('videoCall.completed'),
      rejected: t('videoCall.rejected'),
      cancelled: t('videoCall.cancelled')
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badges[status] || badges.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <VideoCallIcon className="w-6 h-6 text-purple-600" />
          {t('videoCall.requests')}
        </h2>
        <div className="text-sm text-gray-600">
          {t('common.all')}: {requests.length} | 
          {t('videoCall.pending')}: {requests.filter(r => r.status === 'pending').length}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {['all', 'pending', 'scheduled', 'active', 'completed', 'cancelled'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === status
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status === 'all' ? t('common.all') : 
             status === 'pending' ? t('videoCall.pending') :
             status === 'scheduled' ? t('videoCall.scheduled') :
             status === 'active' ? t('videoCall.active') :
             status === 'completed' ? t('videoCall.completed') :
             status === 'cancelled' ? t('videoCall.cancelled') : status}
          </button>
        ))}
      </div>

      {/* Bulk Actions */}
      {selectedRequests.length > 0 && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-800">
              เลือกแล้ว: {selectedRequests.length} requests
            </span>
            <div className="flex gap-2">
              <button
                onClick={handleBulkApprove}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
              >
                ✅ {t('videoCall.approveAll')}
              </button>
              <button
                onClick={() => {
                  const reason = prompt('เหตุผลในการปฏิเสธ:');
                  if (reason) handleBulkReject(reason);
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
              >
                ❌ {t('videoCall.rejectAll')}
              </button>
              <button
                onClick={() => setSelectedRequests([])}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">{t('videoCall.noRequests')}</p>
            <p className="text-sm mt-2">{t('videoCall.requestsWillShowHere')}</p>
          </div>
        ) : (
          filteredRequests.map(request => (
            <div
              key={request.id}
              className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                selectedRequests.includes(request.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      checked={selectedRequests.includes(request.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRequests([...selectedRequests, request.id]);
                        } else {
                          setSelectedRequests(selectedRequests.filter(id => id !== request.id));
                        }
                      }}
                      className="w-4 h-4 text-green-500 rounded"
                    />
                    {getStatusBadge(request.status)}
                    <span className="font-semibold text-gray-800">
                      👤 {request.buyerName || 'ผู้ซื้อ'}
                    </span>
                  </div>
                  
                  <div className="ml-7 space-y-1">
                    <p className="text-sm text-gray-600">
                      📦 สินค้า: {request.productName || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600">
                      ⏰ ขอเวลา: {formatTime(request.requestedTime)}
                    </p>
                    {request.scheduledTime && (
                      <p className="text-sm text-green-600">
                        📅 จองเวลา: {formatTime(request.scheduledTime)}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      📅 ส่งเมื่อ: {formatTime(request.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowDetailModal(true);
                    }}
                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    ดูรายละเอียด
                  </button>
                  
                  {request.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(request.id)}
                        className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600"
                      >
                        ✅ {t('videoCall.approve')}
                      </button>
                      <button
                        onClick={() => {
                          const reason = prompt('เหตุผลในการปฏิเสธ:');
                          if (reason) handleReject(request.id, reason);
                        }}
                        className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        ❌ {t('videoCall.reject')}
                      </button>
                    </>
                  )}
                  
                  {request.status === 'scheduled' && (
                    <button
                      onClick={() => {
                        // Navigate to video call
                        window.location.href = `/marketplace/video-call/${request.chatId || request.id}`;
                      }}
                      className="px-3 py-1 text-sm bg-purple-500 text-white rounded hover:bg-purple-600"
                    >
                      <VideoCallIcon className="w-4 h-4" />
                      <span>เริ่ม Call</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Request Detail Modal */}
      {showDetailModal && selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedRequest(null);
          }}
          onApprove={() => {
            handleApprove(selectedRequest.id);
            setShowDetailModal(false);
          }}
          onReject={(reason) => {
            handleReject(selectedRequest.id, reason);
            setShowDetailModal(false);
          }}
        />
      )}
    </div>
  );
};

export default VideoCallRequestsList;

